import Collapse from 'antd/lib/collapse'
import { FormInstance, useForm } from 'antd/lib/form/Form'
import { DifficultyLevel } from 'Constants/tasks.constants'
import { useMemo } from 'react'
import { GenerateResponseForm } from 'Tasks/CustomTask/UI/common/GenerateResponseForm/GenerateResponseForm'
import { HumanResponse } from 'Tasks/CustomTask/UI/common/HumanResponse/HumanResponse'
import {
    CustomDataType,
    GenerateResponseFormType,
    HumanResponseFormType,
    ManualResponseFormType,
    PrimaryResponseFormType,
    TurnResponseFormType,
} from 'Types/Tasks.types'
import classes from './ReviewMode.module.less'
import { TurnContainerData } from 'Tasks/CustomTask/UI/common/TurnContainerData/TurnContainerData'
import { ProjectFeatureFlags } from 'HTTPClients/projects.client'
import { MarkdownViewer } from '../common/MarkdownViewer/MarkdownViewer'
import { ManualResponseForm } from '../common/ManualResponseForm/ManualResponseForm'
import { getTransformedModelFeedback } from 'Utils/customTask.utils'
import { Flex } from 'antd/lib'
import { ModelFeedback } from '../common/ModelFeedback/ModelFeedback'
import { CustomForm } from 'Types/CustomTask.types'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { LiveFeedbackContextType } from 'Types/LiveFeedback.types'

type ReviewModePropsType = {
    outputResponse?: CustomDataType['response']
    setForms: (forms: CustomForm) => void
    isReadOnly: boolean
    featureFlags?: ProjectFeatureFlags['feature_flags']
    attributesUI?: UpdatableReactElement
    installedPlugins: IPlugin[]
    question: string
    setIsLiveFeedbackChecked: () => void
    liveFeedbackContext: LiveFeedbackContextType
    areAttributesValid: () => Promise<boolean>
}

export const ReviewMode = (props: ReviewModePropsType) => {
    const [generateResponseForm] = useForm<GenerateResponseFormType>()
    const [manualResponseForm] = useForm<ManualResponseFormType>()
    const turnPrimaryResponseForms: FormInstance<PrimaryResponseFormType>[] = Array.from({
        length: props.outputResponse?.main_info?.value.turns.length ?? 0,
    })
    const turnStepResponseForms: FormInstance<TurnResponseFormType>[] = Array.from({
        length: props.outputResponse?.main_info?.value.turns.length ?? 0,
    })

    const [humanResponseForm] = useForm<HumanResponseFormType>()

    const generateResponseInitialValues = useMemo(() => {
        const promptAnnotationValues = props.outputResponse?.main_info?.value.prompt_annotation
        return {
            domain: promptAnnotationValues?.domain ?? '',
            subDomain: promptAnnotationValues?.sub_domain ?? '',
            timeToDevelopStrategy: promptAnnotationValues?.time_for_strategy ?? 0,
            timeToSolve: promptAnnotationValues?.time_to_solve ?? 0,
            difficultyLevel:
                (promptAnnotationValues?.difficulty as DifficultyLevel) ?? DifficultyLevel.Easy,
            complexityLevel: promptAnnotationValues?.complexity ?? '',
            accuracy: promptAnnotationValues?.accuracy ?? '',
            humanFinalAnswer: promptAnnotationValues?.human_final_answer ?? '',
            humanFinalAnswerReasoning: promptAnnotationValues?.human_final_answer_reasoning ?? '',
            humanCompleteFinalAnswer: promptAnnotationValues?.human_complete_final_answer ?? '',
            modelReasoningTrace: promptAnnotationValues?.model_reasoning_trace ?? '',
            modelReasoningTakenFrom: promptAnnotationValues?.model_reasoning_taken_from ?? '',
            modelFinalAnswer: promptAnnotationValues?.model_final_answer ?? '',
            modelFeedbackDetails:
                promptAnnotationValues?.feedback_level_metrics?.map(detail => ({
                    feedbackUsefulness: detail.feedback_usefulness,
                    feedbackResultCode: detail.feedback_result_code,
                    feedbackUsefulnessJustification: detail.feedback_usefulness_justification,
                })) ?? [],
        }
    }, [props.outputResponse?.main_info?.value.prompt_annotation])

    const manualResponseInitialValues = useMemo(() => {
        const promptAnnotationValues = props.outputResponse?.main_info?.value.prompt_annotation
        return {
            humanCompleteFinalAnswer: promptAnnotationValues?.human_complete_final_answer ?? '',
            humanFinalAnswerReasoning: promptAnnotationValues?.human_final_answer_reasoning ?? '',
            modelReasoningTrace: promptAnnotationValues?.model_reasoning_trace ?? '',
            modelFinalAnswer: promptAnnotationValues?.model_final_answer ?? '',
            accuracy: promptAnnotationValues?.accuracy ?? '',
            modelFeedbackDetails:
                promptAnnotationValues?.feedback_level_metrics?.map(detail => ({
                    feedbackUsefulness: detail.feedback_usefulness,
                    feedbackResultCode: detail.feedback_result_code,
                    feedbackUsefulnessJustification: detail.feedback_usefulness_justification,
                })) ?? [],
        }
    }, [props.outputResponse?.main_info?.value.prompt_annotation])

    const humanResponseInitialValues = useMemo(() => {
        return {
            human_final_answer: props.outputResponse?.main_info?.value.final_short_answer ?? '',
            values:
                props.outputResponse?.main_info?.value.human_correct_response?.steps?.map(data => ({
                    python_or_llm: data.python_or_text,
                    correct_step: data.content,
                })) ?? [],
        }
    }, [
        props.outputResponse?.main_info?.value.final_short_answer,
        props.outputResponse?.main_info?.value.human_correct_response?.steps,
    ])

    const isCorrectResponse = useMemo(() => {
        const turnsData = props.outputResponse?.main_info?.value.turns ?? []
        return (
            !!turnsData.length &&
            turnsData[turnsData.length - 1].annotation_data.response_annotation.accuracy === 'YES'
        )
    }, [props.outputResponse?.main_info?.value.turns])

    const hasTurnAnnotations: boolean = !!props.featureFlags?.turn_annotation_enabled
    const acceptPromptOptions: boolean = !!props.featureFlags?.stem_project

    const turnAnnotationsComponent = useMemo(() => {
        if (!hasTurnAnnotations) return null
        return (
            <>
                <Collapse
                    className={classes.collapseContainer}
                    items={props.outputResponse?.main_info?.value.turns.map((turn, index) => {
                        return {
                            key: index,
                            label: `Turn ${index + 1}`,
                            forceRender: true,
                            children: (
                                <TurnContainerData
                                    setForms={props.setForms}
                                    key={index}
                                    turn={index + 1}
                                    response={turn.llm_response}
                                    responseAnnotationValues={
                                        turn.annotation_data.response_annotation
                                    }
                                    firstIncorrectStep={
                                        turn.annotation_data.response_annotation
                                            .first_incorrect_step
                                    }
                                    steps={turn.steps}
                                    turnValues={turn.annotation_data.steps_annotation}
                                    setPrimaryResponseForm={(
                                        form: FormInstance<PrimaryResponseFormType>
                                    ) => {
                                        turnPrimaryResponseForms[index] = form
                                        props.setForms({
                                            primaryResponseForms: turnPrimaryResponseForms,
                                        })
                                    }}
                                    setTurnStepResponseForm={(
                                        form: FormInstance<TurnResponseFormType>
                                    ) => {
                                        turnStepResponseForms[index] = form
                                        props.setForms({
                                            turnResponseForms: turnStepResponseForms,
                                        })
                                    }}
                                    isReadOnly={props.isReadOnly}
                                />
                            ),
                        }
                    })}
                ></Collapse>

                <HumanResponse
                    setForms={props.setForms}
                    form={humanResponseForm}
                    onSubmitHumanResponse={() => {}}
                    disableAddStep={true}
                    initialValues={humanResponseInitialValues}
                    isReviewMode={true}
                    isReadOnly={props.isReadOnly}
                    isCorrectResponse={isCorrectResponse}
                />
            </>
        )
    }, [
        hasTurnAnnotations,
        humanResponseForm,
        humanResponseInitialValues,
        isCorrectResponse,
        props,
        turnPrimaryResponseForms,
        turnStepResponseForms,
    ])

    const transformedModelFeedback = useMemo(() => {
        const promptRevisions = props.outputResponse?.main_info?.value.prompt_revisions ?? []
        const feedbackFromModel = promptRevisions.length
            ? promptRevisions[promptRevisions.length - 1].feedback_from_model
            : undefined
        return getTransformedModelFeedback(feedbackFromModel ?? [])
    }, [props.outputResponse?.main_info?.value.prompt_revisions])

    const previewResponse = useMemo(() => {
        const promptRevisions = props.outputResponse?.main_info?.value.prompt_revisions ?? []
        const modelResponse = promptRevisions.length
            ? promptRevisions[promptRevisions.length - 1].steps_annotation
            : ''
        if (!modelResponse) return null
        return (
            <Flex vertical gap={25} className={classes.previewResponseContainer}>
                <MarkdownViewer title="MODEL RESPONSE" text={modelResponse} />
                <div style={{ borderTop: '1px solid var(--border)' }} />
                <ModelFeedback feedbacks={transformedModelFeedback} />
                <div style={{ borderTop: '1px solid var(--border)' }} />
            </Flex>
        )
    }, [props.outputResponse?.main_info?.value.prompt_revisions, transformedModelFeedback])

    const modelResponseExtraInfo = useMemo(() => {
        const llmData = (
            props.outputResponse?.internal_data as
                | {
                      llm_meta_data: { response: { number_of_responses: number } }[]
                  }
                | undefined
        )?.llm_meta_data

        return {
            totalModelResponsesOnPromptRevision:
                llmData?.[llmData.length - 1].response.number_of_responses ?? 0,
            modelResponseFeedbackCode: transformedModelFeedback.map(feedback => ({
                label: feedback.feedback_result_code_label,
                value: feedback.feedback_result_code,
            })),
            modelResponse:
                props.outputResponse?.main_info?.value.prompt_revisions?.at(-1)?.steps_annotation ??
                '',
        }
    }, [
        props.outputResponse?.internal_data,
        props.outputResponse?.main_info?.value.prompt_revisions,
        transformedModelFeedback,
    ])

    return (
        <div>
            {previewResponse}
            {props.featureFlags?.lmf_project || props.featureFlags?.ots_project ? (
                <ManualResponseForm
                    setForms={props.setForms}
                    form={manualResponseForm}
                    onSubmit={() => {}}
                    initialValues={manualResponseInitialValues}
                    isReviewMode={true}
                    isReadOnly={props.isReadOnly}
                    modelResponseExtraInfo={modelResponseExtraInfo}
                    attributesUI={props.attributesUI}
                    featureFlags={props.featureFlags}
                    installedPlugins={props.installedPlugins}
                    question={props.question}
                    setIsLiveFeedbackChecked={props.setIsLiveFeedbackChecked}
                    isLMFProject={!!props.featureFlags?.lmf_project}
                    isStemProject={!!props.featureFlags?.stem_project}
                    liveFeedbackContext={props.liveFeedbackContext}
                    areAttributesValid={props.areAttributesValid}
                />
            ) : (
                <GenerateResponseForm
                    setForms={props.setForms}
                    form={generateResponseForm}
                    onSubmit={() => {}}
                    initialValues={generateResponseInitialValues}
                    isReviewMode={true}
                    isReadOnly={props.isReadOnly}
                    enableTurnAnnotations={hasTurnAnnotations}
                    hasPromptOptions={acceptPromptOptions}
                    modelResponseExtraInfo={modelResponseExtraInfo}
                    attributesUI={props.attributesUI}
                    isLMFProject={!!props.featureFlags?.lmf_project}
                    isStemProject={!!props.featureFlags?.stem_project}
                    installedPlugins={props.installedPlugins}
                    question={props.question}
                    setIsLiveFeedbackChecked={props.setIsLiveFeedbackChecked}
                    liveFeedbackContext={props.liveFeedbackContext}
                    areAttributesValid={props.areAttributesValid}
                />
            )}
            {turnAnnotationsComponent}
        </div>
    )
}
